/**
 * DialerContactsAddressesCard — compact, permanent dock on the dialer page
 * that lets a call agent quickly update or extend a lead's contacts and
 * addresses without leaving the call. Also exposes a quick "Email" action
 * that opens the existing DialerEmailComposer (Microsoft 365 path).
 *
 * State is held locally and notified up via `onChange(updatedLead)` so the
 * parent (SalesDialerLive) can keep call-stage UI in sync with primary
 * contact/address swaps. No persistence in demo mode.
 *
 * data-testid roots: dialer-contacts-addresses-card · contacts-tab · addresses-tab
 *   · add-contact-btn · add-address-btn · email-from-card-btn
 */
import { useState, useMemo } from "react";
import { User, MapPin, Plus, Star, Trash2, Mail, Phone, PhoneCall, IdCard } from "lucide-react";
import { toast } from "sonner";

const STAR_FILLED = "#F5A524";
const STAR_EMPTY = "#D1D5DB";

function StarBtn({ active, onClick, label = "Mark primary" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      data-testid={active ? "primary-star-active" : "primary-star"}
      style={{
        background: "transparent", border: "none", cursor: "pointer",
        padding: 2, display: "inline-flex", alignItems: "center", color: active ? STAR_FILLED : STAR_EMPTY,
      }}>
      <Star style={{ width: 13, height: 13, fill: active ? STAR_FILLED : "transparent" }} />
    </button>
  );
}

function Field({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 10, color: "var(--text-3, #6b7280)" }}>
      <span style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 9 }}>{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        data-testid={testid}
        style={{
          fontSize: 12, padding: "5px 8px", borderRadius: 6,
          border: "0.5px solid var(--border, #e5e7eb)", background: "#fff",
          fontFamily: "var(--font)", color: "var(--text, #1f2937)", outline: "none",
        }}
      />
    </label>
  );
}

export default function DialerContactsAddressesCard({ lead, onChange, onOpenEmail, onOpenLeadInfo, onQuickDial, callActive = false }) {
  const [tab, setTab] = useState("contacts");
  const contacts = useMemo(() => Array.isArray(lead?.contacts) ? lead.contacts : [], [lead]);
  const locations = useMemo(() => Array.isArray(lead?.locations) ? lead.locations : [], [lead]);

  const pushLead = (next) => onChange?.(next);

  // ── Contacts ──
  const setContacts = (nextList) => pushLead({ ...lead, contacts: nextList });

  const updateContact = (idx, patch) => {
    const next = contacts.map((c, i) => i === idx ? { ...c, ...patch } : c);
    setContacts(next);
  };

  const markContactPrimary = (idx) => {
    const next = contacts.map((c, i) => ({ ...c, primary: i === idx, rank: i === idx ? 1 : (c.rank > 1 ? c.rank : 2) }));
    setContacts(next);
    toast.success(`${next[idx].name || "Contact"} is now primary`);
  };

  const addContact = () => {
    const id = `c-new-${Date.now()}`;
    const next = [...contacts, { id, name: "", role: "", email: "", phone: "", primary: contacts.length === 0, rank: contacts.length + 1 }];
    setContacts(next);
    toast.message("New contact row added");
  };

  const removeContact = (idx) => {
    if (contacts[idx]?.primary && contacts.length > 1) {
      toast.error("Mark another contact primary first");
      return;
    }
    const next = contacts.filter((_, i) => i !== idx);
    setContacts(next);
  };

  // Quick-dial a non-primary contact: marks them primary so the dialer picks
  // them up, then triggers the parent's dial action.
  const quickDial = (idx) => {
    const target = contacts[idx];
    if (!target?.phone) {
      toast.error("This contact has no phone number");
      return;
    }
    const nextContacts = contacts.map((c, i) => ({
      ...c,
      primary: i === idx,
      rank: i === idx ? 1 : (c.rank > 1 ? c.rank : 2),
    }));
    const nextLead = { ...lead, contacts: nextContacts };
    pushLead(nextLead);
    onQuickDial?.(nextContacts[idx], nextLead);
  };

  // ── Locations / addresses ──
  const setLocations = (nextList) => pushLead({ ...lead, locations: nextList });

  const updateLocation = (idx, patch) => {
    const next = locations.map((l, i) => i === idx ? { ...l, ...patch } : l);
    setLocations(next);
  };

  const markLocationPrimary = (idx) => {
    const next = locations.map((l, i) => ({ ...l, primary: i === idx, rank: i === idx ? 1 : (l.rank > 1 ? l.rank : 2) }));
    setLocations(next);
    toast.success(`${next[idx].label || next[idx].city || "Address"} is now primary`);
  };

  const addLocation = () => {
    const id = `l-new-${Date.now()}`;
    const next = [...locations, { id, label: "", address: "", city: "", state: "", zip: "", phone: "", primary: locations.length === 0, rank: locations.length + 1 }];
    setLocations(next);
    toast.message("New address row added");
  };

  const removeLocation = (idx) => {
    if (locations[idx]?.primary && locations.length > 1) {
      toast.error("Mark another address primary first");
      return;
    }
    const next = locations.filter((_, i) => i !== idx);
    setLocations(next);
  };

  return (
    <section
      data-testid="dialer-contacts-addresses-card"
      className="card"
      style={{
        padding: 0, display: "flex", flexDirection: "column", overflow: "hidden",
        background: "var(--paper, #fff)", borderRadius: 12, minHeight: 240,
      }}>
      {/* Header */}
      <header style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: "0.5px solid var(--border-soft, #eee)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 13, fontWeight: 600, color: "var(--navy, #1b365D)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} data-testid="card-lead-name">
            {lead?.clinic || "Lead"}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-3, #6b7280)", marginTop: 1 }}>
            {contacts.length} contact{contacts.length === 1 ? "" : "s"} · {locations.length} address{locations.length === 1 ? "" : "es"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {onOpenLeadInfo && (
            <button
              type="button"
              data-testid="lead-info-from-card-btn"
              onClick={onOpenLeadInfo}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px",
                background: "#fff", color: "var(--blue, #2563eb)",
                border: "0.5px solid var(--blue, #2563eb)", borderRadius: 6,
                fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}>
              <IdCard style={{ width: 11, height: 11 }} /> Lead info
            </button>
          )}
          <button
            type="button"
            data-testid="email-from-card-btn"
            onClick={onOpenEmail}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px",
              background: "var(--blue, #2563eb)", color: "#fff", border: "none", borderRadius: 6,
              fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            }}>
            <Mail style={{ width: 11, height: 11 }} /> Email
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "0.5px solid var(--border-soft, #eee)" }}>
        {[
          { id: "contacts", label: `Contacts (${contacts.length})`, Icon: User, testid: "contacts-tab" },
          { id: "addresses", label: `Addresses (${locations.length})`, Icon: MapPin, testid: "addresses-tab" },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1, padding: "8px 10px", background: "transparent", border: "none",
                borderBottom: active ? "2px solid var(--blue, #2563eb)" : "2px solid transparent",
                color: active ? "var(--blue, #2563eb)" : "var(--text-3, #6b7280)",
                fontSize: 11, fontWeight: active ? 600 : 500, cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}>
              <t.Icon style={{ width: 11, height: 11 }} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: 10, display: "flex", flexDirection: "column", gap: 10 }}>
        {tab === "contacts" && (
          <>
            {contacts.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--text-3, #6b7280)", padding: 16, textAlign: "center" }}>
                No contacts yet — click <em>Add contact</em>.
              </div>
            )}
            {contacts.map((c, i) => (
              <div key={c.id || i} data-testid={`contact-row-${i}`}
                style={{ background: c.primary ? "#F4F8FF" : "var(--subtle, #fafafa)", borderRadius: 8, padding: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <StarBtn active={!!c.primary} onClick={() => markContactPrimary(i)} />
                  <input
                    value={c.name || ""}
                    onChange={(e) => updateContact(i, { name: e.target.value })}
                    placeholder="Name (e.g. Dr. Jane Doe)"
                    data-testid={`contact-name-${i}`}
                    style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "var(--navy, #1b365D)", padding: "3px 6px", borderRadius: 5, border: "0.5px solid transparent", background: "transparent", fontFamily: "inherit", outline: "none" }}
                  />
                  {!c.primary && c.phone && (
                    <button
                      type="button"
                      onClick={() => quickDial(i)}
                      title={callActive ? `Switch call to ${c.name || "this contact"}` : `Dial ${c.name || "this contact"}`}
                      data-testid={`quick-dial-${i}`}
                      disabled={!onQuickDial}
                      style={{
                        background: "var(--success, #1D9E75)", border: "none", borderRadius: 5,
                        padding: "3px 6px", cursor: onQuickDial ? "pointer" : "not-allowed",
                        color: "#fff", display: "inline-flex", alignItems: "center", gap: 3,
                        fontSize: 10, fontWeight: 600, fontFamily: "inherit", opacity: onQuickDial ? 1 : 0.5,
                      }}>
                      <PhoneCall style={{ width: 11, height: 11 }} />
                      {callActive ? "Switch" : "Dial"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeContact(i)}
                    title="Remove contact"
                    data-testid={`remove-contact-${i}`}
                    style={{ background: "transparent", border: "none", padding: 2, cursor: "pointer", color: "#9ca3af" }}>
                    <Trash2 style={{ width: 12, height: 12 }} />
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <Field label="Role" value={c.role} onChange={(v) => updateContact(i, { role: v })} placeholder="Owner / Mgr" testid={`contact-role-${i}`} />
                  <Field label="Phone" value={c.phone} onChange={(v) => updateContact(i, { phone: v })} placeholder="(555) 123-4567" testid={`contact-phone-${i}`} type="tel" />
                </div>
                <Field label="Email" value={c.email} onChange={(v) => updateContact(i, { email: v })} placeholder="name@practice.com" testid={`contact-email-${i}`} type="email" />
              </div>
            ))}
            <button
              type="button"
              onClick={addContact}
              data-testid="add-contact-btn"
              style={{
                marginTop: 4, padding: "7px 10px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                background: "#fff", border: "0.5px dashed var(--blue, #2563eb)", borderRadius: 6,
                color: "var(--blue, #2563eb)", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}>
              <Plus style={{ width: 11, height: 11 }} /> Add contact
            </button>
          </>
        )}

        {tab === "addresses" && (
          <>
            {locations.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--text-3, #6b7280)", padding: 16, textAlign: "center" }}>
                No addresses yet — click <em>Add address</em>.
              </div>
            )}
            {locations.map((l, i) => (
              <div key={l.id || i} data-testid={`address-row-${i}`}
                style={{ background: l.primary ? "#F4F8FF" : "var(--subtle, #fafafa)", borderRadius: 8, padding: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <StarBtn active={!!l.primary} onClick={() => markLocationPrimary(i)} />
                  <input
                    value={l.label || ""}
                    onChange={(e) => updateLocation(i, { label: e.target.value })}
                    placeholder="Label (Main clinic / Suite B…)"
                    data-testid={`address-label-${i}`}
                    style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "var(--navy, #1b365D)", padding: "3px 6px", borderRadius: 5, border: "0.5px solid transparent", background: "transparent", fontFamily: "inherit", outline: "none" }}
                  />
                  <button
                    type="button"
                    onClick={() => removeLocation(i)}
                    title="Remove address"
                    data-testid={`remove-address-${i}`}
                    style={{ background: "transparent", border: "none", padding: 2, cursor: "pointer", color: "#9ca3af" }}>
                    <Trash2 style={{ width: 12, height: 12 }} />
                  </button>
                </div>
                <Field label="Street" value={l.address} onChange={(v) => updateLocation(i, { address: v })} placeholder="123 Main St" testid={`address-street-${i}`} />
                <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 0.7fr", gap: 6 }}>
                  <Field label="City" value={l.city} onChange={(v) => updateLocation(i, { city: v })} placeholder="City" testid={`address-city-${i}`} />
                  <Field label="State" value={l.state} onChange={(v) => updateLocation(i, { state: v })} placeholder="CA" testid={`address-state-${i}`} />
                  <Field label="ZIP" value={l.zip} onChange={(v) => updateLocation(i, { zip: v })} placeholder="10001" testid={`address-zip-${i}`} />
                </div>
                <Field label="Phone" value={l.phone} onChange={(v) => updateLocation(i, { phone: v })} placeholder="(555) 123-4567" testid={`address-phone-${i}`} type="tel" />
              </div>
            ))}
            <button
              type="button"
              onClick={addLocation}
              data-testid="add-address-btn"
              style={{
                marginTop: 4, padding: "7px 10px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                background: "#fff", border: "0.5px dashed var(--blue, #2563eb)", borderRadius: 6,
                color: "var(--blue, #2563eb)", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}>
              <Plus style={{ width: 11, height: 11 }} /> Add address
            </button>
          </>
        )}
      </div>

      {/* Footer hint */}
      <footer style={{ padding: "6px 12px", borderTop: "0.5px solid var(--border-soft, #eee)", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 9, color: "var(--text-4, #9ca3af)" }}>
        <span><Phone style={{ width: 9, height: 9, display: "inline", marginRight: 3 }} />Primary contact powers the dialer.</span>
        <span>Edits stage live · Save on hangup</span>
      </footer>
    </section>
  );
}
